// Copy into your web frontend. No Electron detection or Electron API changes.
const pending = new Map();
let attachedBridge;
let wifiDiscovery;

export function isMealBillAndroid() {
  return typeof window !== 'undefined' && typeof window.MealBillNative?.postMessage === 'function';
}

function attach() {
  if (!isMealBillAndroid()) throw new Error('MealBill Android printing is unavailable');
  const bridge = window.MealBillNative;
  if (attachedBridge !== bridge) {
    bridge.onmessage = (event) => {
      let message;
      try { message = JSON.parse(event.data); } catch { return; }
      const entry = pending.get(message.requestId);
      if (!entry) return;
      clearTimeout(entry.timer);
      pending.delete(message.requestId);
      const result = message.result;
      if (result?.ok) entry.resolve(result);
      else {
        const error = new Error(result?.message || 'Android print request failed');
        error.result = result;
        entry.reject(error);
      }
    };
    attachedBridge = bridge;
  }
  return bridge;
}

function request(action, payload = {}) {
  return new Promise((resolve, reject) => {
    const bridge = attach();
    const requestId = globalThis.crypto.randomUUID();
    const message = JSON.stringify({ requestId, action, ...payload });
    if (message.length > 262144) throw new Error('Receipt exceeds the Android bridge size limit');
    const timer = setTimeout(() => {
      pending.delete(requestId);
      const error = new Error('Print response timed out. Check job status and paper before reprinting.');
      error.result = { code: 'RESPONSE_TIMEOUT', jobId: payload.receipt?.jobId, status: 'uncertain' };
      reject(error);
    }, 120000);
    pending.set(requestId, { resolve, reject, timer });
    try { bridge.postMessage(message); }
    catch (error) { clearTimeout(timer); pending.delete(requestId); reject(error); }
  });
}

export const validPrinterRoute = (route) => ['bill', 'kitchen'].includes(route) || /^kitchen:[a-f0-9-]{36}$/.test(route);
export const androidPrinter = {
  getKotMappings: (scope) => request('getKotMappings', { scope }),
  saveKotMappings: (scope, rows) => request('saveKotMappings', { scope, rows }),
  capabilities: () => request('capabilities'),
  openSettings: () => {
    if (!isMealBillAndroid()) return Promise.reject(new Error('MealBill Android printing is unavailable'));
    window.location.assign('/settings');
    return Promise.resolve({ ok: true });
  },
  getProfiles: () => request('getPrinterProfiles'),
  clearProfile: (printer) => {
    if (!validPrinterRoute(printer)) return Promise.reject(new Error('Invalid printer route'));
    return request('clearPrinterProfile', { printer });
  },
  getPrintRoute: (printer) => {
    if (!validPrinterRoute(printer)) return Promise.reject(new Error('Invalid printer route'));
    return request('getPrintRoute', { printer });
  },
  saveProfile: (printer, profile) => {
    if (!validPrinterRoute(printer)) return Promise.reject(new Error('Invalid printer route'));
    if (!profile || !['tcp', 'bluetooth'].includes(profile.transport)
      || typeof profile.address !== 'string' || !profile.address.trim()
      || !Number.isInteger(profile.port) || profile.port < 1 || profile.port > 65535
      || ![384, 576, 512, 832].includes(profile.width) || typeof profile.cut !== 'boolean') {
      return Promise.reject(new Error('Check the printer address, port and paper width'));
    }
    return request('savePrinterProfile', { printer, profile });
  },
  listPairedBluetoothPrinters: () => request('listBluetoothPrinters'),
  discoverWifiPrinters: () => {
    if (!wifiDiscovery) wifiDiscovery = request('discoverWifiPrinters').finally(() => { wifiDiscovery = undefined; });
    return wifiDiscovery;
  },
  requestBluetoothPermission: () => request('requestBluetoothPermission'),
  openBluetoothSettings: () => request('openBluetoothSettings'),
  testPrint: (printer) => {
    if (!validPrinterRoute(printer)) return Promise.reject(new Error('Invalid printer route'));
    return request('testPrint', { printer });
  },
  getJobStatus: (jobId) => request('status', { jobId }),
  print: (receipt) => {
    if (!receipt || !/^[A-Za-z0-9._:-]{1,128}$/.test(receipt.jobId)) {
      return Promise.reject(new Error('A stable, unique jobId is required'));
    }
    if (!validPrinterRoute(receipt.printer)) {
      return Promise.reject(new Error('printer must be bill or kitchen'));
    }
    if (!Array.isArray(receipt.lines) || !receipt.lines.length || receipt.lines.length > 500) {
      return Promise.reject(new Error('Receipt must contain 1–500 lines'));
    }
    return request('print', { receipt });
  },
};
